import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchTickers,
  fetchCandles,
  fetchWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  fetchTechnicalAnalysis,
  fetchLstmPrediction,
  fetchSentimentAnalysis
} from '../api/client';
import './Dashboard.css';
import { useAuth } from '../context/AuthContext';
import LoginModal from '../components/LoginModal';
import AlertModal from '../components/AlertModal';

import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler
);

function Dashboard() {
  const [filterType, setFilterType] = useState('marketCap');

  const [marketCapRange, setMarketCapRange] = useState('all');
  const [volumeRange, setVolumeRange] = useState('all');
  const [exchangeFilter, setExchangeFilter] = useState('all');

  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [timeRange, setTimeRange] = useState('1M');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [prediction, setPrediction] = useState(null);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [predictionError, setPredictionError] = useState(null);
  const [lstmPrediction, setLstmPrediction] = useState(null);
  const [lstmLoading, setLstmLoading] = useState(false);
  const [lstmError, setLstmError] = useState(null);
  const [watchlist, setWatchlist] = useState(new Set());
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [showWatchedOnly, setShowWatchedOnly] = useState(false);

  const [watchlistToast, setWatchlistToast] = useState(null);

  // Technical Analysis State
  const [showTechnicalAnalysisModal, setShowTechnicalAnalysisModal] = useState(false);
  const [technicalAnalysisData, setTechnicalAnalysisData] = useState(null);
  const [technicalAnalysisLoading, setTechnicalAnalysisLoading] = useState(false);
  const [technicalAnalysisTimeframe, setTechnicalAnalysisTimeframe] = useState('1d');



  const { data: tickersData, isLoading, isError } = useQuery({
    queryKey: ['dashboard-tickers'],
    queryFn: () => fetchTickers({ limit: 2000 })
  });

  const { data: candlesData } = useQuery({
    queryKey: ['candles', selectedCrypto?.symbol, timeRange],
    queryFn: () =>
      fetchCandles(selectedCrypto?.symbol, {
        limit: getLimitForRange(timeRange),
      }),
    enabled: !!selectedCrypto,
  });

  function getLimitForRange(range) {
    switch (range) {
      case '1W': return 7;
      case '1M': return 30;
      case '3M': return 90;
      case '1Y': return 365;
      case 'MAX': return 1000;
      default: return 30;
    }
  }

  useEffect(() => {
    // The axios interceptor in api client already handles CSRF token
    // No need for manual CSRF fetching
  }, []);


  const { user, isGuest } = useAuth();


  const getInitialFilter = useCallback(() => {
    if (!user) {
      return { period: '1M', volume: 'all', price: 'all' };
    }
    const stored = localStorage.getItem(
      `cv_dashboard_filter_${user.username}`
    );
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          period: parsed.period || '1M',
          volume: parsed.volume || 'all',
          price: parsed.price || 'all',
        };
      } catch (e) {
        console.error('Failed to parse dashboard filter', e);
      }
    }
    return { period: '1M', volume: 'all', price: 'all' };
  }, [user]);


  useEffect(() => {
    if (!user) return;

    const initial = getInitialFilter();


    let mappedTimeRange = initial.period || '1M';

    if (mappedTimeRange === '7D') {
      mappedTimeRange = '1W';
    }


    if (!['1W', '1M', '3M', '1Y', 'MAX'].includes(mappedTimeRange)) {
      mappedTimeRange = '1M';
    }

    setTimeRange(mappedTimeRange);


    if (initial.volume) {
      setVolumeRange(initial.volume);
    }
  }, [user, getInitialFilter]);

  useEffect(() => {
    if (!user || isGuest) return;

    (async () => {
      try {
        const data = await fetchWatchlist();
        setWatchlist(new Set((data.symbols || []).map(s => s.toUpperCase())));
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user, isGuest]);

  // Lock body scroll when details overlay is open
  useEffect(() => {
    if (selectedCrypto) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedCrypto]);



  const processedCryptos = useMemo(() => {
    if (!tickersData?.results) return [];

    const symbolMap = new Map();

    tickersData.results.forEach((ticker) => {
      const [base, quote] = ticker.market_symbol.split('/');
      if (!base || !quote) return;

      const symbol = base.toUpperCase();
      const price = ticker.last || 0;
      const volume24h = ticker.quote_volume_24h || 0;
      const high24h = ticker.high_24h || price;
      const low24h = ticker.low_24h || price;
      const liquidity = ticker.liquidity || 0;

      const liquidations =
        ticker.liquidations_24h ||
        ticker.liquidations ||
        0;

      if (!symbolMap.has(symbol)) {
        symbolMap.set(symbol, {
          id: symbol.toLowerCase(),
          symbol,
          name: symbol,
          price: 0,
          high24h: 0,
          low24h: 0,
          volume: 0,
          marketCap: 0,
          liquidity: 0,
          liquidations: 0,
          exchange: ticker.exchange?.name || 'Unknown',
          tickerCount: 0,
        });
      }

      const crypto = symbolMap.get(symbol);
      crypto.tickerCount += 1;
      crypto.volume += volume24h;
      crypto.liquidity += liquidity;
      crypto.liquidations += liquidations;

      if (price > crypto.price) {
        crypto.price = price;
        crypto.high24h = high24h;
        crypto.low24h = low24h;
      }

      const estimatedMarketCap = price * (volume24h / (price || 1)) * 100;
      crypto.marketCap = Math.max(crypto.marketCap, estimatedMarketCap);
    });

    return Array.from(symbolMap.values()).map((c) => {
      const change24h =
        c.high24h && c.low24h
          ? ((c.high24h - c.low24h) / c.low24h) * 100
          : 0;
      return { ...c, change24h };
    });
  }, [tickersData]);

  const exchangeOptions = useMemo(() => {
    const set = new Set();
    processedCryptos.forEach((c) => {
      if (c.exchange) set.add(c.exchange);
    });
    return ['all', ...Array.from(set).sort()];
  }, [processedCryptos]);

  const sortedCryptos = useMemo(() => {
    let cryptos = [...processedCryptos];

    cryptos = cryptos.filter((c) => {
      let ok = true;

      if (marketCapRange === 'large') {
        ok = ok && c.marketCap >= 1e10;
      } else if (marketCapRange === 'mid') {
        ok = ok && c.marketCap >= 1e9 && c.marketCap < 1e10;
      } else if (marketCapRange === 'small') {
        ok = ok && c.marketCap > 0 && c.marketCap < 1e9;
      }

      if (volumeRange === 'high') {
        ok = ok && c.volume >= 1e9;
      } else if (volumeRange === 'medium') {
        ok = ok && c.volume >= 1e8 && c.volume < 1e9;
      } else if (volumeRange === 'low') {
        ok = ok && c.volume > 0 && c.volume < 1e8;
      }

      if (exchangeFilter !== 'all') {
        ok = ok && c.exchange === exchangeFilter;
      }

      return ok;
    });


    if (showWatchedOnly) {
      cryptos = cryptos.filter(c => watchlist.has(c.symbol.toUpperCase()));
    }

    switch (filterType) {
      case 'marketCap':
        return cryptos.sort((a, b) => b.marketCap - a.marketCap);
      case 'volume':
        return cryptos.sort((a, b) => b.volume - a.volume);
      case 'exchange':
        return cryptos.sort((a, b) => a.exchange.localeCompare(b.exchange));
      default:
        return cryptos;
    }
  }, [
    processedCryptos,
    filterType,
    marketCapRange,
    volumeRange,
    exchangeFilter,

    showWatchedOnly,
    watchlist
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, marketCapRange, volumeRange, exchangeFilter]);

  const totalPages = Math.max(1, Math.ceil(sortedCryptos.length / itemsPerPage));

  const paginatedCryptos = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedCryptos.slice(start, start + itemsPerPage);
  }, [sortedCryptos, currentPage]);

  const topByMarketCap = useMemo(() => {
    return [...processedCryptos]
      .sort((a, b) => b.marketCap - a.marketCap)
      .slice(0, 10);
  }, [processedCryptos]);

  const top10ChartData = {
    labels: topByMarketCap.map((c) => c.symbol),
    datasets: [
      {
        label: 'Пазарна капитализација (USD)',
        data: topByMarketCap.map((c) => c.marketCap),
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const top10ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => formatNumber(ctx.parsed.y),
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#94a3b8' },
        grid: { display: false },
      },
      y: {
        ticks: {
          color: '#94a3b8',
          callback: (value) => formatNumber(value),
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.2)',
          drawBorder: false,
        },
      },
    },
  };

  const chartData = useMemo(() => {
    if (!candlesData?.results) return null;

    const reversedData = [...candlesData.results].reverse();

    return {
      labels: reversedData.map((c) => c.time),
      datasets: [
        {
          label: 'Price',
          data: reversedData.map((c) => c.close),
          borderColor: '#60a5fa',
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, 'rgba(96, 165, 250, 0.5)');
            gradient.addColorStop(1, 'rgba(96, 165, 250, 0.0)');
            return gradient;
          },
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    };
  }, [candlesData]);

  const formatNumber = (num) => {
    if (num == null) return '$0.00';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${Number(num).toFixed(2)}`;
  };

  const formatPrice = (price) => {
    if (price == null) return '$0.00';
    const n = Number(price);
    if (Number.isNaN(n)) return '$0.00';
    if (n >= 1000) {
      return `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${n.toFixed(2)}`;
  };

  const formatPlainNumber = (num) => {
    if (!num || num === 0) return '-';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(0);
  };

  // Helper functions for Technical Analysis
  const getSignalColor = (signal) => {
    if (signal === 'BUY') return '#10b981';
    if (signal === 'SELL') return '#ef4444';
    return '#6b7280';
  };

  const getSignalBadge = (signal) => {
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        color: '#fff',
        backgroundColor: getSignalColor(signal)
      }}>
        {signal}
      </span>
    );
  };

  const handleTechnicalAnalysis = async (timeframe = '1d') => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    if (!selectedCrypto) return;

    // Clear others
    setPrediction(null);
    setPredictionError(null);
    setLstmPrediction(null);
    setLstmError(null);

    setTechnicalAnalysisLoading(true);
    // If opening modal for the first time or changing timeframe, set it
    if (timeframe) setTechnicalAnalysisTimeframe(timeframe);

    try {
      // Use the dedicated API function
      const data = await fetchTechnicalAnalysis(selectedCrypto.symbol, timeframe || technicalAnalysisTimeframe);
      setTechnicalAnalysisData(data);
      // setShowTechnicalAnalysisModal(true); // Removed modal
    } catch (error) {
      console.error('Failed to fetch technical analysis:', error);
      // Ideally show a toast or error
    } finally {
      setTechnicalAnalysisLoading(false);
    }
  };

  const handleLstmPrediction = async () => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    if (!selectedCrypto) return;

    try {
      setLstmLoading(true);
      setLstmError(null);
      setLstmPrediction(null);

      // Clear others
      setPrediction(null);
      setPredictionError(null);
      setTechnicalAnalysisData(null);

      const data = await fetchLstmPrediction(selectedCrypto.symbol, 10, 1);
      setLstmPrediction(data);
    } catch (err) {
      console.error(err);
      setLstmError("Failed to load LSTM prediction.");
    } finally {
      setLstmLoading(false);
    }
  };


  const handleShowAnalysis = async () => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    if (!selectedCrypto) return;

    try {
      setPredictionLoading(true);
      setPredictionError(null);

      // Clear others
      setLstmPrediction(null);
      setLstmError(null);
      setTechnicalAnalysisData(null);

      const data = await fetchSentimentAnalysis(selectedCrypto.symbol);
      setPrediction(data);
    } catch (err) {
      console.error(err);
      setPredictionError("Неуспешно вчитување на анализата.");
    } finally {
      setPredictionLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <h1>Контролна табла</h1>
          <p className="muted">Вчитување податоци...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <h1>Контролна табла</h1>
          <p className="muted" style={{ color: '#f87171' }}>
            Грешка при вчитување на податоци. Проверете дали backend серверот работи.
          </p>
        </div>
      </div>
    );
  }

  const hasAnalysis = Boolean(prediction || lstmPrediction || technicalAnalysisData);

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Контролна табла</h1>
        <p className="muted">
          Преглед на криптовалути ({sortedCryptos.length} филтрирани од{' '}
          {processedCryptos.length} достапни)
        </p>
      </div>

      <div className="top-chart-card">
        <h2>Топ 10 криптовалути по пазарна капитализација</h2>
        <div className="top-chart-wrapper">
          <Bar data={top10ChartData} options={top10ChartOptions} />
        </div>
      </div>

      <div className="filter-section">
        <label>Подреди според:</label>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterType === 'marketCap' ? 'active' : ''}`}
            onClick={() => setFilterType('marketCap')}
          >
            Пазарна капитализација
          </button>
          <button
            className={`filter-btn ${filterType === 'volume' ? 'active' : ''}`}
            onClick={() => setFilterType('volume')}
          >
            Волумен
          </button>
          <button
            className={`filter-btn ${filterType === 'exchange' ? 'active' : ''}`}
            onClick={() => setFilterType('exchange')}
          >
            Размена
          </button>
        </div>
      </div>

      <div className="advanced-filters">
        <div className="advanced-filter">
          <label>Пазарна капитализација:</label>
          <select
            value={marketCapRange}
            onChange={(e) => setMarketCapRange(e.target.value)}
          >
            <option value="all">Сите</option>
            <option value="large">&gt;= 10B (Large cap)</option>
            <option value="mid">1B – 10B (Mid cap)</option>
            <option value="small">&lt; 1B (Small cap)</option>
          </select>
        </div>

        <div className="advanced-filter">
          <label>Волумен (24ч):</label>
          <select
            value={volumeRange}
            onChange={(e) => setVolumeRange(e.target.value)}
          >
            <option value="all">Сите</option>
            <option value="high">&gt;= 1B (High)</option>
            <option value="medium">100M – 1B (Medium)</option>
            <option value="low">&lt; 100M (Low)</option>
          </select>
        </div>

        <div className="advanced-filter">
          <label>Размена:</label>
          <select
            value={exchangeFilter}
            onChange={(e) => setExchangeFilter(e.target.value)}
          >
            {exchangeOptions.map((ex) => (
              <option key={ex} value={ex}>
                {ex === 'all' ? 'Сите' : ex}
              </option>
            ))}
          </select>
        </div>
      </div>
      <button
        className={`filter-btn ${showWatchedOnly ? 'active' : ''}`}
        onClick={() => setShowWatchedOnly(v => !v)}
      >
        Прикажи само криптовалути кои се дел од листата за следење
      </button>


      <div className="crypto-grid">
        {paginatedCryptos.map((crypto) => (
          <div
            key={crypto.id}
            className={`crypto-card ${selectedCrypto?.id === crypto.id ? 'selected' : ''
              }`}
            onClick={() => {
              setSelectedCrypto(crypto);
              setPrediction(null);
              setPredictionError(null);
              setLstmPrediction(null);
              setLstmError(null);
              setTechnicalAnalysisData(null);
            }}

          >
            <div className="crypto-header">
              <div className="crypto-icon">{crypto.symbol.charAt(0)}</div>
              <div>
                <div className="crypto-name">
                  {crypto.symbol}
                  {watchlist.has(crypto.symbol.toUpperCase()) && (
                    <span className="watch-badge" style={{ marginLeft: '6px', fontSize: '0.8em' }}>⭐</span>
                  )}
                </div>
                <div className="crypto-exchange" style={{ fontSize: '13px', color: 'rgba(226, 232, 240, 0.6)' }}>
                  {crypto.exchange}
                </div>
              </div>
            </div>
            <div className="crypto-price-group">
              <div className="crypto-price">{formatPrice(crypto.price)}</div>
              <div
                className={`crypto-change ${crypto.change24h >= 0 ? 'positive' : 'negative'
                  }`}
              >
                {crypto.change24h >= 0 ? '+' : ''}
                {crypto.change24h.toFixed(2)}%
              </div>
            </div>
            <div className="crypto-extra">
              <span>МК: {formatNumber(crypto.marketCap)}</span>
              <span>Vol 24ч: {formatNumber(crypto.volume)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="pagination">
        <button
          className="page-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          ◀ Претходна
        </button>
        <span className="page-info">
          Страница {currentPage} од {totalPages}
        </span>
        <button
          className="page-btn"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Следна ▶
        </button>
      </div>

      {selectedCrypto && (
        <div className="details-overlay" onClick={() => setSelectedCrypto(null)}>
          <div
            className={`details-card ${hasAnalysis ? 'can-scroll' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="details-header-row">
              <div className="coin-info">
                <div className="coin-icon-large">
                  {selectedCrypto.symbol.charAt(0)}
                </div>
                <div>
                  <h2 className="coin-name">{selectedCrypto.name}</h2>
                  <div className="coin-symbol-line">
                    <span className="coin-symbol">{selectedCrypto.symbol}</span>
                    <span className="coin-exchange">
                      {selectedCrypto.exchange}
                    </span>
                  </div>
                  <div className="coin-price-block">
                    <span className="coin-price">
                      {formatPrice(selectedCrypto.price)}
                    </span>
                    <span
                      className={`coin-change ${selectedCrypto.change24h >= 0 ? 'positive' : 'negative'
                        }`}
                    >
                      {selectedCrypto.change24h >= 0 ? '+' : ''}
                      {selectedCrypto.change24h.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="time-selectors">
                {['1W', '1M', '3M', '1Y', 'MAX'].map((range) => (
                  <button
                    key={range}
                    className={`time-btn ${timeRange === range ? 'active' : ''
                      }`}
                    onClick={() => setTimeRange(range)}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>

            <div className="details-chart-container">
              {chartData ? (
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                          label: (ctx) => formatPrice(ctx.parsed.y),
                        },
                      },
                    },
                    scales: {
                      x: {
                        display: false,
                        grid: { display: false },
                      },
                      y: {
                        position: 'right',
                        grid: {
                          color: 'rgba(255, 255, 255, 0.1)',
                          drawBorder: false,
                        },
                        ticks: {
                          color: '#94a3b8',
                          callback: (value) => formatNumber(value),
                        },
                      },
                    },
                    interaction: {
                      mode: 'nearest',
                      axis: 'x',
                      intersect: false,
                    },
                  }}
                />
              ) : (
                <div className="loading-chart">Вчитување график...</div>
              )}
            </div>

            <div className="details-actions">
              <button
                className="action-btn"
                onClick={() => handleTechnicalAnalysis('1d')}
              >
                Техничка анализа
              </button>

              <button
                className="action-btn"
                onClick={handleLstmPrediction}
              >
                LSTM предикции
              </button>

              <button
                className="action-btn"
                onClick={handleShowAnalysis}
              >
                Сентимент анализа
              </button>

              <button
                className={`action-btn ${selectedCrypto && watchlist.has(selectedCrypto.symbol.toUpperCase()) ? "tracked" : ""
                  }`}
                disabled={watchlistLoading}
                onClick={async () => {
                  if (isGuest) {
                    setShowLoginModal(true);
                    return;
                  }
                  if (!selectedCrypto) return;

                  const symbol = selectedCrypto.symbol.toUpperCase();
                  const isTracked = watchlist.has(symbol);

                  try {
                    setWatchlistLoading(true);

                    if (isTracked) {
                      await removeFromWatchlist(symbol);
                    } else {
                      await addToWatchlist(symbol);
                    }

                    setWatchlist(prev => {
                      const next = new Set(prev);
                      if (isTracked) next.delete(symbol);
                      else next.add(symbol);
                      return next;
                    });

                    setWatchlistToast(
                      isTracked
                        ? ` ${symbol} отстранет од листа`
                        : ` ${symbol} додаден во листа`
                    );
                    setTimeout(() => setWatchlistToast(null), 2500);

                  } catch (e) {
                    console.error(e);
                    setWatchlistToast("❌ Грешка при следење");
                    setTimeout(() => setWatchlistToast(null), 2500);
                  } finally {
                    setWatchlistLoading(false);
                  }
                }}
              >
                {selectedCrypto && watchlist.has(selectedCrypto.symbol.toUpperCase())
                  ? "Tracked ✓"
                  : "Листа на следење"}
              </button>




            </div>

            <div className="prediction-section">
              {predictionLoading && (
                <div className="prediction-card prediction-loading">
                  <div className="prediction-header">
                    <div className="prediction-title-skeleton" />
                    <div className="prediction-subtitle-skeleton" />
                  </div>
                  <div className="prediction-body-skeleton">
                    <div className="prediction-row-skeleton" />
                    <div className="prediction-row-skeleton" />
                    <div className="prediction-row-skeleton short" />
                  </div>
                  <div className="prediction-footer-skeleton" />
                  <p className="prediction-muted">Вчитување анализа (on-chain + NLP)...</p>
                </div>
              )}

              {predictionError && (
                <div className="prediction-card prediction-error">
                  <div className="prediction-header">
                    <span className="prediction-pill error-pill">Грешка</span>
                  </div>
                  <p className="prediction-error-text">{predictionError}</p>
                </div>
              )}

              {prediction && !predictionLoading && !predictionError && (() => {
                const p = prediction.prediction || prediction;
                const direction = p.direction || p.prediction_label;
                const isUp = direction === 'UP';

                const probUp = (p.prob_up * 100).toFixed(1);
                const probDown = (p.prob_down * 100).toFixed(1);
                const accuracy = (p.model_accuracy * 100).toFixed(1);
                const lastDate = p.last_date;

                const sentimentLabel =
                  p.sentiment_label === 'positive'
                    ? 'Позитивен'
                    : p.sentiment_label === 'negative'
                      ? 'Негативен'
                      : 'Неутрален';

                return (
                  <div className="prediction-card">
                    <div className="prediction-header">
                      <div>
                        <h3 className="prediction-title">
                          AI предвидување за {prediction.symbol}
                        </h3>
                        <p className="prediction-subtitle">
                          Комбинација од on-chain метрики и NLP сентимент анализа
                        </p>
                      </div>
                      <span
                        className={`prediction-pill ${isUp ? 'prediction-pill-up' : 'prediction-pill-down'
                          }`}
                      >
                        {isUp ? 'Очекувано движење: ГОРЕ ↑' : 'Очекувано движење: ДОЛУ ↓'}
                      </span>
                    </div>

                    <div className="prediction-body">
                      <div className="prediction-main-stat">
                        <div className="prediction-main-value">
                          {isUp ? probUp : probDown}%
                        </div>
                        <div className="prediction-main-label">
                          Веројатност дека цената ќе оди {isUp ? 'ГОРЕ' : 'ДОЛУ'} утре
                        </div>

                        <div className="prediction-bar">
                          <div
                            className="prediction-bar-up"
                            style={{ width: `${probUp}%` }}
                          />
                          <div
                            className="prediction-bar-down"
                            style={{ width: `${probDown}%` }}
                          />
                        </div>

                        <div className="prediction-bar-labels">
                          <span>↑ {probUp}%</span>
                          <span>↓ {probDown}%</span>
                        </div>
                      </div>

                      <div className="prediction-side-stats">
                        <div className="prediction-stat">
                          <span className="prediction-stat-label">Точност на моделот </span>
                          <span className="prediction-stat-value">{accuracy}%</span>
                        </div>
                        <div className="prediction-stat">
                          <span className="prediction-stat-label">Сентимент </span>
                          <span
                            className={`prediction-stat-value sentiment-${p.sentiment_label || 'neutral'}`}
                          >
                            {sentimentLabel}
                          </span>
                        </div>
                        <div className="prediction-stat">
                          <span className="prediction-stat-label">Последен датум </span>
                          <span className="prediction-stat-value">
                            {lastDate || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="prediction-footer">
                      <p className="prediction-muted">
                        *Ова предвидување е експериментално и не претставува финансиски
                        совет. Целта е да се демонстрира комбинација од on-chain и
                        сентимент анализа.
                      </p>
                    </div>
                  </div>
                );
              })()}

              {lstmLoading && (
                <div className="prediction-card prediction-loading">
                  <p className="prediction-muted">Вчитување LSTM предикција...</p>
                </div>
              )}

              {lstmError && (
                <div className="prediction-card prediction-error">
                  <div className="prediction-header">
                    <span className="prediction-pill error-pill">Грешка</span>
                  </div>
                  <p className="prediction-error-text">{lstmError}</p>
                </div>
              )}

              {lstmPrediction && !lstmLoading && !lstmError && (
                <div className="prediction-card">
                  <div className="prediction-header">
                    <div>
                      <h3 className="prediction-title">
                        LSTM предикција за {lstmPrediction.symbol}
                      </h3>
                      <p className="prediction-subtitle">
                        Предвидување базирано на историски OHLCV податоци
                      </p>
                    </div>
                    <span className="prediction-pill prediction-pill-up">
                      Последна предвидена цена: {" "}
                      {lstmPrediction.last_pred?.toFixed(4)}
                    </span>
                  </div>

                  <div className="prediction-body">
                    <div className="prediction-side-stats">
                      <div className="prediction-stat">
                        <span className="prediction-stat-label">RMSE </span>
                        <span className="prediction-stat-value">
                          {lstmPrediction.metrics.rmse.toFixed(4)}
                        </span>
                      </div>
                      <div className="prediction-stat">
                        <span className="prediction-stat-label">MAPE </span>
                        <span className="prediction-stat-value">
                          {(lstmPrediction.metrics.mape * 100).toFixed(2)}%
                        </span>
                      </div>
                      <div className="prediction-stat">
                        <span className="prediction-stat-label">R² </span>
                        <span className="prediction-stat-value">
                          {lstmPrediction.metrics.r2.toFixed(4)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="prediction-footer">
                    <p className="prediction-muted">
                      *LSTM моделот е експериментален и служи за академска демонстрација.
                    </p>
                  </div>
                </div>
              )}



              {/* Technical Analysis Card */}
              {technicalAnalysisLoading && (
                <div className="prediction-card prediction-loading">
                  <p className="prediction-muted">Вчитување техничка анализа...</p>
                </div>
              )}

              {technicalAnalysisData && !technicalAnalysisLoading && (
                <div className="prediction-card">
                  <div className="prediction-header">
                    <div>
                      <h3 className="prediction-title">
                        Техничка Анализа - {technicalAnalysisData.symbol}
                      </h3>
                      <p className="prediction-subtitle">
                        Сигнали од {technicalAnalysisTimeframe === '1d' ? '1 ден' : technicalAnalysisTimeframe === '1w' ? '1 недела' : '1 месец'}
                      </p>
                    </div>
                    {getSignalBadge(technicalAnalysisData.overall_signal)}
                  </div>

                  <div className="prediction-body" style={{ flexDirection: 'column', gap: '1rem' }}>
                    {/* Timeframe Selector */}
                    <div className="time-selectors" style={{ alignSelf: 'flex-start' }}>
                      {['1d', '1w', '1m'].map((tf) => (
                        <button
                          key={tf}
                          className={`time-btn ${technicalAnalysisTimeframe === tf ? 'active' : ''}`}
                          onClick={() => handleTechnicalAnalysis(tf)}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          {tf === '1d' ? '1Д' : tf === '1w' ? '1Н' : '1М'}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-start' }}>
                      {/* Signal Distribution */}
                      <div style={{ flex: '1 1 300px' }}>
                        <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#94a3b8' }}>Дистрибуција на сигнали</h4>
                        <div style={{ height: '150px' }}>
                          <Bar
                            data={{
                              labels: ['BUY', 'HOLD', 'SELL'],
                              datasets: [{
                                label: 'Signals',
                                data: [
                                  technicalAnalysisData.signal_summary.buy_count,
                                  technicalAnalysisData.signal_summary.hold_count,
                                  technicalAnalysisData.signal_summary.sell_count
                                ],
                                backgroundColor: ['#10b981', '#6b7280', '#ef4444'],
                                borderRadius: 4,
                                barThickness: 20
                              }]
                            }}
                            options={{
                              indexAxis: 'y',
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { display: false } },
                              scales: {
                                x: { grid: { display: false, drawBorder: false }, ticks: { display: false } },
                                y: { grid: { display: false, drawBorder: false }, ticks: { color: '#cbd5f5' } }
                              }
                            }}
                          />
                        </div>
                      </div>

                      {/* Key Indicators Summary */}
                      <div style={{ flex: '1 1 200px', display: 'grid', gap: '8px', gridTemplateColumns: '1fr 1fr' }}>
                        <div className="prediction-stat">
                          <span className="prediction-stat-label">RSI (14)</span>
                          <span className="prediction-stat-value">{technicalAnalysisData.indicators.rsi.value?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="prediction-stat">
                          <span className="prediction-stat-label">MACD</span>
                          <span className="prediction-stat-value">{technicalAnalysisData.indicators.macd.value?.toFixed(4) || 'N/A'}</span>
                        </div>
                        <div className="prediction-stat">
                          <span className="prediction-stat-label">SMA (20)</span>
                          <span className="prediction-stat-value">{technicalAnalysisData.indicators.sma.value?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="prediction-stat">
                          <span className="prediction-stat-label">EMA (20)</span>
                          <span className="prediction-stat-value">{technicalAnalysisData.indicators.ema.value?.toFixed(2) || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="prediction-footer">
                    <p className="prediction-muted">
                      *Техничката анализа е базирана на историски податоци и не гарантира идни резултати.
                    </p>
                  </div>
                </div >
              )
              }
            </div >

            <div className="details-stats-grid">
              <div className="stat-box">
                <span className="stat-label">Тековна цена</span>
                <span className="stat-value">
                  {formatPrice(selectedCrypto.price)}
                </span>
              </div>
              <div className="stat-box">
                <span className="stat-label">Дневна промена</span>
                <span
                  className={`stat-value ${selectedCrypto.change24h >= 0 ? 'positive' : 'negative'
                    }`}
                >
                  {selectedCrypto.change24h >= 0 ? '+' : ''}
                  {selectedCrypto.change24h.toFixed(2)}%
                </span>
              </div>
              <div className="stat-box">
                <span className="stat-label">Пазарна капитализација</span>
                <span className="stat-value">
                  {formatNumber(selectedCrypto.marketCap)}
                </span>
              </div>
              <div className="stat-box">
                <span className="stat-label">Волумен (24ч)</span>
                <span className="stat-value">
                  {formatNumber(selectedCrypto.volume)}
                </span>
              </div>
              <div className="stat-box">
                <span className="stat-label">
                  Ликвидација<br />24 ч
                </span>
                <span className="stat-value">
                  {formatPlainNumber(selectedCrypto.liquidations)}
                </span>
              </div>

              <div className="stat-box">
                <span className="stat-label">
                  Ликвидност<br />Order Book
                </span>
                <span className="stat-value">
                  {formatPlainNumber(selectedCrypto.liquidity)}
                </span>
              </div>

              <div className="stat-box" >
                <button className="action-btn primary" onClick={() => { if (isGuest) { setShowLoginModal(true); return; } setShowAlertModal(true); }}>Додади аларм</button>
              </div>
            </div>
          </div >
        </div >
      )}

      {
        watchlistToast && (
          <div className="watchlist-toast">
            {watchlistToast}
          </div>
        )
      }

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message="Најавете се за да користите напредни функции"
      />
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        crypto={selectedCrypto}
      />

    </div >
  );
}

export default Dashboard;




